import React, { useMemo, useState } from 'react'
import s from './teacher-card-match-result.module.scss'
import { getNoun } from '@utils/lib/getNoun'
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import { IconCalendarPlus, IconClockHour3, IconEyeglass, IconGift, IconSparkles, IconStar } from '@tabler/icons-react'
import { TRIAL_LESSON_PRICE, TRIAL_LESSON_DURATION } from '@shared/lib/trial'
import { Button } from '@shared/ui/button/button'
import { useProfile } from '@shared/api/useProfile'
import { StartChatButton } from '@features/messanger/start-chat'
import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'

const formatPrice = (price) => Number(price || 0).toLocaleString('ru-RU')

const getExperienceLabel = (teacher) => {
  const value = teacher?.experience_years || teacher?.experienceYears || teacher?.teaching_experience || teacher?.experience

  if (!value) return ''
  if (Number.isNaN(Number(value))) return value

  const years = Number(value)
  return `${years} ${getNoun(years, 'год', 'года', 'лет')} опыта`
}

export const TeacherCardMatchingProgress = ({
  teacher_slug,
  id,
  free_first_lesson,
  first_lesson_discount,
  avatar,
  grade,
  appointments_count,
  lname,
  fname,
  rating,
  name,
  instruments,
  reviews,
  reviews_count,
  reviewsCount,
  teacher_bio,
  description,
  photos,
  trial_enabled,
  isRecomended,
  isRecommended,
  isMostExperienced,
  nearest_available_time,
  ...teacher
}) => {
  const navigate = useNavigate()
  const { data: profile } = useProfile()
  const teacherData = {
    ...teacher,
    teacher_slug,
    free_first_lesson,
    first_lesson_discount,
    avatar,
    grade,
    appointments_count,
    lname,
    fname,
    rating,
    name,
    instruments,
    reviews,
    teacher_bio,
    photos,
  }
  const fullName = `${fname || ""} ${name || ""} ${lname || ""}`.trim()
  const instrumentLabel = instruments?.map((i) => i?.name).filter(Boolean).join(', ')
  const experienceLabel = getExperienceLabel(teacherData)
  const currentPrice = Number(grade?.price || teacher?.price || 0)
  const oldPrice = Number(
    grade?.old_price ||
    grade?.oldPrice ||
    grade?.price_without_discount ||
    grade?.full_price ||
    teacher?.old_price ||
    teacher?.oldPrice ||
    0
  )
  const showRecommended = isRecomended || isRecommended
  const showMostExperienced = isMostExperienced || (!showRecommended && Number.parseInt(experienceLabel, 10) >= 10)
  const about = teacher_bio || description || 'Сертифицированный преподаватель с внимательным подходом и понятной программой занятий под вашу цель.'
  const discountPercent = Number(first_lesson_discount || 0)
  const discountedPrice = currentPrice && free_first_lesson
    ? 0
    : currentPrice && discountPercent > 0
      ? Math.round(currentPrice - (currentPrice * discountPercent) / 100)
      : currentPrice
  const displayPrice = currentPrice ? discountedPrice : 0
  const displayOldPrice = (free_first_lesson || discountPercent > 0) && discountedPrice < currentPrice ? currentPrice : oldPrice
  const isTrial = !!trial_enabled
  const imageSlides = [
    avatar
      ? {
          id: 'avatar',
          src: process.env.REACT_APP_STORAGE_URL + avatar.image,
        }
      : null,
    ...(photos?.map((photo) => ({
      id: photo?.id || photo?.file,
      src: process.env.REACT_APP_STORAGE_URL + photo?.file,
    })) || []),
  ].filter((slide) => slide?.src)
  const fallbackAvatarSrc = process.env.REACT_APP_STORAGE_URL + "images/default_avatar.png"
  const slides = imageSlides.length > 0 ? imageSlides : [{ id: 'default-avatar', src: fallbackAvatarSrc }]
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const activeImage = slides[activeImageIndex] || slides[0]

  const handleOpenTeacher = () => {
    navigate(`/teacher/${teacher_slug}`);

    if (window?.ym) {
      window.ym(96194074,'reachGoal','open-teacher-page')
    }
  }

  const userInstrument = profile?.instruments?.[0]
  const selectedInstrument = instruments?.find((i) => i?.id === userInstrument?.id) || instruments?.[0]

  const bookingPath = useMemo(() => {
    if (!nearest_available_time || !selectedInstrument) {
      return `/teacher/booking/${teacher_slug}`
    }

    const timezone = profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    const dateObj = new TZDate(nearest_available_time, timezone)
    const date = format(dateObj, 'dd.MM.yyyy')
    const time = format(dateObj, 'HH:mm')

    return `/teacher/booking/${teacher_slug}?date=${encodeURIComponent(date)}&time=${encodeURIComponent(time)}&instrument=${encodeURIComponent(selectedInstrument.id)}`
  }, [nearest_available_time, selectedInstrument, teacher_slug, profile?.timezone])

  const nearestLabel = nearest_available_time
    ? format(
        new TZDate(
          nearest_available_time,
          profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
        ),
        'd MMMM в HH:mm',
        { locale: ru }
      )
    : null

  // CTA про цену, а не про слот — время показываем в блоке цены
  const bookingLabel = isTrial
    ? `Пробное за ${TRIAL_LESSON_PRICE.toLocaleString('ru-RU')} ₽`
    : free_first_lesson
      ? 'Первое занятие бесплатно'
      : discountPercent > 0 && currentPrice
        ? `Первое занятие за ${formatPrice(displayPrice)} ₽`
        : currentPrice
          ? `Занятие за ${formatPrice(displayPrice)} ₽`
          : 'Записаться'

  const durationLabel = isTrial ? `пробное занятие ${TRIAL_LESSON_DURATION} минут` : '50 минут'

	return (
    <article className={classNames(s.matchCard, showRecommended ? s.recommended : '')}>
      <div className={s.badges}>
        {showRecommended ? (
          <span className={classNames(s.badge, s.badgeFilled)}>
            <IconStar className={s.badgeIcon}/>
            Рекомендуем
          </span>
        ) : null}
        {isTrial ? (
          <span className={s.badge}>
            <IconSparkles className={s.badgeIcon}/>
            Пробное занятие
          </span>
        ) : (free_first_lesson || Number(first_lesson_discount || 0) > 0) ? (
          <span className={s.badge}>
            <IconGift className={s.badgeIcon}/>
            {free_first_lesson ? 'Первое занятие бесплатное' : `-${first_lesson_discount}% на первое занятие`}
          </span>
        ) : null}
        {showMostExperienced ? (
          <span className={s.badge}>
            <IconEyeglass className={s.badgeIcon}/>
            Самый опытный
          </span>
        ) : null}
      </div>
      <div className={s.content}>
        <button type="button" className={s.avatar} onClick={handleOpenTeacher}>
          <img src={activeImage.src} alt={fullName} />
          <span className={s.hoverZones} aria-hidden="true">
            {slides.map((slide, index) => (
              <span
                key={slide.id}
                onMouseEnter={() => setActiveImageIndex(index)}
              />
            ))}
          </span>
          <span className={s.sliderDots} aria-hidden="true">
            {slides?.length > 1 && slides.map((slide, index) => (
              <span
                key={slide.id}
                className={index === activeImageIndex ? s.sliderDotActive : ''}
              />
            ))}
          </span>
        </button>
        <div className={s.info}>
					<div className={s.cardHeader}>
						<div className={s.nameWrapper}>
							<button type="button" className={s.name} onClick={handleOpenTeacher}>
								{fullName}
							</button>
							<p className={s.meta}>
								{instrumentLabel ? <TextWithEmoji text={instrumentLabel}/> : 'Музыка'}
								{experienceLabel ? <span>• {experienceLabel}</span> : null}
							</p>
						</div>
						{/* <div className={s.rating}>
							<IconStar className={s.ratingIcon}/>
							<span>{currentRating}</span>
							<span>•</span>
							<span>{reviewCount} {getNoun(reviewCount, "отзыв", "отзыва", "отзывов")}</span>
						</div> */}
					</div>
          <p className={s.about}>{about}</p>
          {isTrial ? (
            <div className={s.price}>
              {/* <span className={s.currentPrice}>{TRIAL_LESSON_PRICE.toLocaleString('ru-RU')} ₽</span> */}
              <span className={s.priceTime}>
                {nearestLabel ? (<><IconClockHour3 size={13}/>Ближайшая запись: {nearestLabel}</>) : `/ ${durationLabel}`}
              </span>
            </div>
          ) : currentPrice ? (
            <div className={s.price}>
              {/* {displayOldPrice > displayPrice ? <span className={s.oldPrice}>{formatPrice(displayOldPrice)} ₽</span> : null} */}
              {/* <span className={s.currentPrice}>{formatPrice(displayPrice)} ₽</span> */}
              <span className={s.priceTime}>
                {nearestLabel ? (<><IconClockHour3 size={13}/>Ближайшая запись: {nearestLabel}</>) : `/ ${durationLabel}`}
              </span>
            </div>
          ) : (
            <div className={s.price}>
              <span className={s.currentPriceSmall}>Цена договорная</span>
            </div>
          )}
        </div>
      </div>
      <div className={s.actions}>
        <StartChatButton teacherId={id} className={s.messageButton}/>
        <Button
          className={s.actionButton}
          to={bookingPath}
					onClick={() => {
						navigate(bookingPath)

						if (window?.ym) {
							window.ym(96194074,'reachGoal','open-teacher-page')
						}
					}}
          label={bookingLabel}
          lefticon={isTrial ? <IconSparkles size={16} /> : <IconCalendarPlus size={16} />}
        />
      </div>
    </article>
  );
}
